// src/components/settings/ProfileTab.jsx
import React, { useState, useRef, memo, useCallback } from 'react';
import { User, Upload, Edit2, Save } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

const ProfileTab = memo(({ 
  user, 
  formData, 
  states, 
  lgas, 
  lcdas, 
  loading, 
  uploadingPhoto, 
  onInputChange, 
  onSaveProfile, 
  onProfilePictureChange 
}) => {
  const fileInputRef = useRef(null);
  const [focusedField, setFocusedField] = useState(null);

  const handleProfilePictureClick = useCallback(() => {
    fileInputRef.current.click();
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    onInputChange(name, value);
  }, [onInputChange]);

  const handleFocus = useCallback((fieldName) => {
    setFocusedField(fieldName);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  const handleSave = useCallback(() => {
    onSaveProfile();
  }, [onSaveProfile]);

  const handleFileChange = useCallback((e) => {
    onProfilePictureChange(e);
  }, [onProfilePictureChange]);

  return (
    <>
      {/* Profile Picture Section */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          <div className="w-20 h-20 rounded-full border-2 border-white shadow overflow-hidden bg-gradient-to-br from-[#ff660e] to-orange-500">
            {user?.profile_picture_url ? (
              <img 
                src={getImageUrl(user.profile_picture_url)}
                alt={`${user.first_name} ${user.last_name}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
            )}
          </div>
          {uploadingPhoto && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          
          <button 
            onClick={handleProfilePictureClick}
            disabled={uploadingPhoto}
            className="absolute -bottom-1 -right-1 bg-white p-1.5 rounded-full shadow border"
          >
            {uploadingPhoto ? (
              <div className="w-3 h-3 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Edit2 className="w-3 h-3 text-[#ff660e]" />
            )}
          </button>
        </div>

        <div className="flex-1">
          <h2 className="text-lg font-bold text-black mb-1">
            {user?.first_name} {user?.middle_name} {user?.last_name}
          </h2>
          <p className="text-gray-600 text-sm mb-2 truncate">{user?.email}</p>
          <button
            onClick={handleProfilePictureClick}
            disabled={uploadingPhoto}
            className="text-xs text-[#ff660e] font-medium hover:text-[#e55a0d] transition-colors disabled:opacity-50 flex items-center"
          >
            <Upload className="w-3 h-3 mr-1" />
            {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
          </button>
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {/* First Name - Read Only */}
          <ReadOnlyField
            label="First Name *"
            value={formData.first_name}
            helpText="Contact support to change first name"
          />

          {/* Last Name - Read Only */}
          <ReadOnlyField
            label="Last Name *"
            value={formData.last_name}
            helpText="Contact support to change last name"
          />
        </div>

        {/* Middle Name - Read Only */}
        <ReadOnlyField
          label="Middle Name"
          value={formData.middle_name}
          helpText="Contact support to change middle name"
        />

        {/* Email - Read Only */}
        <ReadOnlyField
          label="Email Address"
          value={formData.email}
          helpText="Contact support to change email"
        />

        {/* Phone Number - Editable */}
        <FormField
          label="Phone Number *"
          name="phone_number"
          value={formData.phone_number}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          focusedField={focusedField}
          placeholder="+234 800 000 0000"
          required
        />

        {/* State, LGA, LCDA - Editable */}
        <SelectField
          label="State *"
          name="state_id"
          value={formData.state_id}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          focusedField={focusedField}
          options={states}
          optionKey="state_id"
          optionLabel="name"
          placeholder="Select State"
          required
        />

        <SelectField
          label="Local Government (LGA) *"
          name="lga_id"
          value={formData.lga_id}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          focusedField={focusedField}
          options={lgas}
          optionKey="lga_id"
          optionLabel="name"
          placeholder="Select LGA"
          disabled={!formData.state_id}
          required
          helpText={!formData.state_id ? "Select a state first" : null}
        />

        <SelectField
          label="Local Council (LCDA)"
          name="lcda_id"
          value={formData.lcda_id}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          focusedField={focusedField}
          options={lcdas}
          optionKey="lcda_id"
          optionLabel="name"
          placeholder="Select LCDA (Optional)"
          disabled={!formData.lga_id}
          helpText={!formData.lga_id ? "Select an LGA first" : null}
        />
      </div>

      {/* Save Button */}
      <div className="mt-6 pt-4 border-t">
        <button
          onClick={handleSave}
          disabled={loading || uploadingPhoto}
          className="w-full bg-[#ff660e] text-white py-3.5 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </>
  );
});

// Optimized FormField component (for editable fields)
const FormField = memo(({ 
  label, 
  name, 
  value, 
  onChange, 
  onFocus, 
  onBlur, 
  focusedField, 
  placeholder, 
  required 
}) => (
  <div>
    <label className="text-xs text-gray-500 block mb-1">
      {label}
    </label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => onFocus(name)}
      onBlur={onBlur}
      className={`w-full p-3 border rounded-lg text-sm transition-all ${
        focusedField === name ? 'border-[#ff660e] ring-1 ring-[#ff660e]' : ''
      }`}
      placeholder={placeholder}
      required={required}
    />
  </div>
));

// Optimized ReadOnlyField component (for non-editable fields)
const ReadOnlyField = memo(({ label, value, helpText }) => (
  <div>
    <label className="text-xs text-gray-500 block mb-1">
      {label}
    </label>
    <input
      type="text"
      value={value || ''}
      readOnly
      className="w-full p-3 border rounded-lg text-sm bg-gray-50"
    />
    {helpText && (
      <p className="text-xs text-gray-400 mt-1">{helpText}</p>
    )}
  </div>
));

// Optimized SelectField component
const SelectField = memo(({ 
  label, 
  name, 
  value, 
  onChange, 
  onFocus, 
  onBlur, 
  focusedField, 
  options, 
  optionKey, 
  optionLabel, 
  placeholder, 
  disabled, 
  required, 
  helpText 
}) => (
  <div>
    <label className="text-xs text-gray-500 block mb-1">
      {label}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      onFocus={() => onFocus(name)}
      onBlur={onBlur}
      className={`w-full p-3 border rounded-lg text-sm bg-white transition-all ${
        focusedField === name ? 'border-[#ff660e] ring-1 ring-[#ff660e]' : ''
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      disabled={disabled}
      required={required}
    >
      <option value="">{placeholder}</option>
      {options.map(option => (
        <option key={option[optionKey]} value={option[optionKey]}>
          {option[optionLabel]}
        </option>
      ))}
    </select>
    {helpText && (
      <p className="text-xs text-gray-400 mt-1">{helpText}</p>
    )}
  </div>
));

export default ProfileTab;